(() => {
  if (customElements.get('deferred-product-reviews')) return;

  const scripts = new Map();
  const loadScript = (source) => {
    const url = source.src;
    if (scripts.has(url)) return scripts.get(url);
    if (url.includes('/polaris/build.js') && typeof window.ReviewsWidget === 'function') return Promise.resolve();
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      for (const attribute of source.attributes) script.setAttribute(attribute.name, attribute.value);
      script.async = false;
      const timer = setTimeout(() => finish(new Error('Review script timed out')), 15000);
      const finish = (error) => {
        clearTimeout(timer);
        script.onload = script.onerror = null;
        if (error) {
          script.remove();
          reject(error);
        } else {
          resolve();
        }
      };
      script.onload = () => finish();
      script.onerror = () => finish(new Error('Review script failed to load'));
      document.head.append(script);
    }).catch((error) => {
      scripts.delete(url);
      throw error;
    });
    scripts.set(url, promise);
    return promise;
  };

  class DeferredProductReviews extends HTMLElement {
    connectedCallback() {
      this.onRating = this.onRating || this.handleRating.bind(this);
      this.onHash = this.onHash || this.handleHash.bind(this);
      this.onLoad = this.onLoad || (() => this.load());
      this.button = this.querySelector('[data-reviews-load]');
      this.button.addEventListener('click', this.onLoad);
      document.addEventListener('click', this.onRating, true);
      document.addEventListener('keydown', this.onRating, true);
      window.addEventListener('hashchange', this.onHash);
      if (this.dataset.loaded === 'true') return;
      this.handleHash();
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load();
        }, { rootMargin: '300px 0px' });
        this.observer.observe(this);
      } else {
        this.load();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.button?.removeEventListener('click', this.onLoad);
      document.removeEventListener('click', this.onRating, true);
      document.removeEventListener('keydown', this.onRating, true);
      window.removeEventListener('hashchange', this.onHash);
    }

    matchesHash(hash) {
      if (!hash || hash === '#') return false;
      try {
        const id = decodeURIComponent(hash.slice(1));
        return this.id === id || Boolean(this.querySelector('[data-reviews-template]').content.getElementById(id));
      } catch (_) {
        return false;
      }
    }

    handleHash() {
      if (this.matchesHash(window.location.hash)) this.open();
    }

    handleRating(event) {
      if (!(event.target instanceof Element)) return;
      if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
      if (event.type === 'click' && (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)) return;
      const rating = event.target.closest('.product__section--reviews-rating .ruk_rating_snippet');
      const link = event.target.closest('a[href]');
      const url = link ? new URL(link.href, window.location.href) : null;
      const targetsThis = url && url.origin === location.origin && url.pathname === location.pathname && this.matchesHash(url.hash);
      if (!targetsThis && !(rating && document.querySelector('deferred-product-reviews') === this)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open();
    }

    open() {
      this.load();
      this.scrollIntoView({ block: 'start', behavior: 'instant' });
      this.focus({ preventScroll: true });
    }

    async load() {
      if (this.loading || this.dataset.loaded === 'true') return;
      this.loading = true;
      this.observer?.disconnect();
      this.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      this.button.textContent = this.dataset.loadingText;
      this.querySelector('[data-reviews-error]').hidden = true;
      const content = this.querySelector('[data-reviews-content]');
      try {
        const fragment = this.querySelector('[data-reviews-template]').content.cloneNode(true);
        const pending = [...fragment.querySelectorAll('script')].filter((script) => !script.type || /^(application|text)\/(javascript|ecmascript)$/.test(script.type) || script.type === 'module');
        pending.forEach((script) => script.remove());
        content.replaceChildren(fragment);
        if (document.readyState === 'loading') {
          await new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
        }
        for (const source of pending) {
          if (source.src) {
            await loadScript(source);
          } else {
            if (!this.isConnected) return;
            const script = document.createElement('script');
            for (const attribute of source.attributes) script.setAttribute(attribute.name, attribute.value);
            script.textContent = source.textContent;
            content.append(script);
          }
        }
        if (!content.querySelector('[data-reviewsio-initialized="true"]')) throw new Error('Review widget did not initialize');
        this.dataset.loaded = 'true';
        this.querySelector('[data-reviews-placeholder]').hidden = true;
      } catch (_) {
        content.replaceChildren();
        this.button.textContent = this.dataset.retryText;
        this.querySelector('[data-reviews-error]').hidden = false;
      } finally {
        this.loading = false;
        this.button.disabled = false;
        this.removeAttribute('aria-busy');
      }
    }
  }

  customElements.define('deferred-product-reviews', DeferredProductReviews);
})();
